import { Input, Option, TreeItem } from './ConditionalLogic.types'

export interface PopulateInputOption {
  value: number
  label: string
  type: string
  multiple?: boolean
  options: { value: string | number; label: string }[]
}

const normalizeOptions = (
  options?: Record<string, string> | Option[],
): { value: string | number; label: string }[] => {
  if (!options) {
    return []
  }

  if (Array.isArray(options)) {
    return options.map(({ value, label }) => ({
      value,
      label: typeof label === 'string' ? label : label.title,
    }))
  }

  return Object.entries(options).map(([value, label]) => ({ value, label }))
}

export const getPopulateInputsFromTree = (
  taskInputsTree: TreeItem[],
): PopulateInputOption[] => {
  const supportedTypes = ['boolean', 'number', 'select', 'string']
  const populateInputs: PopulateInputOption[] = []

  taskInputsTree.forEach(item => {
    if (
      item.input_item_type === 'input' &&
      supportedTypes.includes(item.type)
    ) {
      populateInputs.push({
        value: item.id,
        label: item.title,
        type: item.type,
        multiple: Boolean(item.multiple),
        options: normalizeOptions(item.options),
      })
    }

    item.inputs?.forEach(nestedInput => {
      if (supportedTypes.includes(nestedInput.type)) {
        populateInputs.push({
          value: nestedInput.id,
          label: nestedInput.title,
          type: nestedInput.type,
          multiple: Boolean(nestedInput.multiple),
          options: normalizeOptions(nestedInput.options),
        })
      }
    })
  })

  return populateInputs
}

export const mergePopulateInputOptions = (
  populateInputs: PopulateInputOption[],
  sourceInputs: {
    value: number
    options?: Option[]
    type?: string
    multiple?: boolean
  }[],
): PopulateInputOption[] =>
  populateInputs.map(populateInput => {
    if (populateInput.type !== 'select') {
      return populateInput
    }

    const sourceInput = sourceInputs.find(
      ({ value }) => value === populateInput.value,
    )

    if (!sourceInput?.options?.length) {
      return populateInput
    }

    return {
      ...populateInput,
      multiple: Boolean(sourceInput.multiple ?? populateInput.multiple),
      options: normalizeOptions(sourceInput.options),
    }
  })

export const findPopulateInputById = (
  populateInputs: PopulateInputOption[],
  id: string | number | null | undefined,
) => {
  if (id === null || id === undefined || id === '') {
    return undefined
  }

  return populateInputs.find(({ value }) => value === +id)
}

export const getDefaultPopulationValue = (
  populateInput: PopulateInputOption,
): string => {
  switch (populateInput.type) {
    case 'boolean':
      return 'true'
    case 'select':
      if (populateInput.multiple) {
        const firstOption = populateInput.options[0]?.value

        return firstOption !== undefined
          ? JSON.stringify([String(firstOption)])
          : '[]'
      }

      return String(populateInput.options[0]?.value ?? '')
    default:
      return ''
  }
}

export const parsePopulateSelectValue = (
  value: string,
  multiple?: boolean,
): string | string[] => {
  if (!multiple) {
    return value
  }

  if (!value) {
    return []
  }

  try {
    const parsed = JSON.parse(value)

    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    return []
  }
}

export const stringifyPopulateSelectValue = (
  value: string | string[],
  multiple?: boolean,
): string => {
  if (!multiple) {
    return String(value)
  }

  return JSON.stringify(Array.isArray(value) ? value.map(String) : [])
}

export const getPopulateRows = (
  inputs?: (string | number | Input | null)[],
): (string | number | Input | null)[] => {
  if (!inputs?.length) {
    return [null]
  }

  return inputs
}

export const isPopulationValueValid = (
  type: string,
  value: string | undefined,
  multiple?: boolean,
) => {
  if (value === undefined || value === null) {
    return false
  }

  if (type === 'number' || type === 'string') {
    return value !== ''
  }

  if (type === 'select' && multiple) {
    try {
      const parsed = JSON.parse(value)

      return Array.isArray(parsed) && parsed.length > 0
    } catch {
      return false
    }
  }

  return value !== ''
}

export const formatPopulationValueLabel = (
  populateInput: PopulateInputOption | undefined,
  value: string,
  formatMessage: (descriptor: { id: string }) => string,
) => {
  if (!populateInput) {
    return value
  }

  if (populateInput.type === 'boolean') {
    return value === 'true'
      ? formatMessage({ id: 'yes' })
      : formatMessage({ id: 'no' })
  }

  if (populateInput.type === 'select') {
    if (populateInput.multiple) {
      const selectedValues = parsePopulateSelectValue(value, true)

      if (!Array.isArray(selectedValues) || !selectedValues.length) {
        return value
      }

      return selectedValues
        .map(selectedValue => {
          const selectedOption = populateInput.options.find(
            option => String(option.value) === String(selectedValue),
          )

          return selectedOption?.label || selectedValue
        })
        .join(', ')
    }

    const selectedOption = populateInput.options.find(
      option => String(option.value) === String(value),
    )

    return selectedOption?.label || value
  }

  return value
}

export const findInTree = (
  taskInputsTree: TreeItem[],
  value: string | number,
  type: 'input' | 'input_group',
) =>
  taskInputsTree.find(
    ({ id, input_item_type }) => id === +value && input_item_type === type,
  )

export const getValidValue = (
  inp: Input | number | string | null | undefined,
) => {
  if (inp === null || inp === undefined) {
    return ''
  }

  return typeof inp !== 'object' ? inp : String(inp.id)
}

export const getTriggerInputFromConditions = (
  conditions?: { input: number | string | null }[],
): number | string | null => {
  const firstCondition = conditions?.find(cond => cond.input)

  return firstCondition?.input ?? null
}
